import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

interface SearchBarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  title: string;
  type: 'page' | 'product' | 'category';
  path: string;
  description?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Arama veritabanı
  const searchDatabase: SearchResult[] = [
    // Sayfalar
    { id: 'home', title: language === 'tr' ? 'Ana Sayfa' : 'Home', type: 'page', path: '/' },
    { id: 'about', title: language === 'tr' ? 'Hakkımızda' : 'About Us', type: 'page', path: '/about' },
    { id: 'products', title: language === 'tr' ? 'Ürünlerimiz' : 'Products', type: 'page', path: '/products' },
    { id: 'media', title: language === 'tr' ? 'Medya' : 'Media', type: 'page', path: '/media' },
    { id: 'contact', title: language === 'tr' ? 'İletişim' : 'Contact', type: 'page', path: '/contact' },
    
    // Ürün kategorileri
    { id: 'fire-extinguishers', title: language === 'tr' ? 'Yangın Söndürücüler' : 'Fire Extinguishers', type: 'category', path: '/products#fire-extinguishers' },
    { id: 'fire-detection', title: language === 'tr' ? 'Yangın Algılama Sistemleri' : 'Fire Detection Systems', type: 'category', path: '/products#fire-detection' },
    { id: 'fire-extinguishing', title: language === 'tr' ? 'Yangın Söndürme Sistemleri' : 'Fire Extinguishing Systems', type: 'category', path: '/products#fire-extinguishing' },
    { id: 'life-saving', title: language === 'tr' ? 'Can Kurtarma Ekipmanları' : 'Life Saving Appliances', type: 'category', path: '/products#life-saving' },
    { id: 'personal-protective', title: language === 'tr' ? 'Kişisel Koruyucu Ekipmanlar' : 'Personal Protective Equipment', type: 'category', path: '/products#personal-protective' },
    
    // Spesifik ürünler
    { id: 'seven-oceans', title: 'Seven Oceans', type: 'product', path: '/products#personal-protective', description: language === 'tr' ? 'Acil İçme Suyu' : 'Emergency Drinking Water' },
    { id: 'fire-suit', title: language === 'tr' ? 'Yangın Kıyafeti' : 'Fire Suit', type: 'product', path: '/products#personal-protective' },
    { id: 'life-jacket', title: language === 'tr' ? 'Can Yeleği' : 'Life Jacket', type: 'product', path: '/products#life-saving' },
    { id: 'fire-hose', title: language === 'tr' ? 'Yangın Hortumu' : 'Fire Hose', type: 'product', path: '/products#fire-fighting' },
    
    // Hizmetler
    { id: 'fire-safety', title: language === 'tr' ? 'Yangın Güvenliği' : 'Fire Safety', type: 'category', path: '/about' },
    { id: 'marine-equipment', title: language === 'tr' ? 'Denizcilik Ekipmanları' : 'Marine Equipment', type: 'category', path: '/products' },
  ];

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setResults([]);
      return;
    }

    const filtered = searchDatabase.filter(item =>
      item.title.toLowerCase().includes(term.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(term.toLowerCase()))
    );

    setResults(filtered.slice(0, 8)); // En fazla 8 sonuç göster
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onClose();
    setSearchTerm('');
    setResults([]);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'page':
        return '📄';
      case 'product':
        return '🔧';
      case 'category':
        return '📁';
      default:
        return '🔍';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl mx-4"
            className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-2xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Header */}
            <div className="flex items-center p-4 border-b border-slate-600">
              <Search className="h-5 w-5 text-slate-400 mr-3" />
              <input
                type="text"
                placeholder={language === 'tr' ? 'Ürün, sayfa veya kategori ara...' : 'Search products, pages or categories...'}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="flex-1 bg-transparent text-white placeholder-slate-400 outline-none text-lg"
                autoFocus
              />
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-white ml-3"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {results.length > 0 ? (
                <div className="p-2">
                  {results.map((result) => (
                    <motion.button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      whileHover={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}
                      className="w-full text-left p-3 rounded-lg hover:bg-amber-400 hover:bg-opacity-10 transition-colors duration-200 flex items-center space-x-3"
                    >
                      <span className="text-2xl">{getTypeIcon(result.type)}</span>
                      <div className="flex-1">
                        <div className="text-white font-medium">{result.title}</div>
                        {result.description && (
                          <div className="text-slate-400 text-sm">{result.description}</div>
                        )}
                        <div className="text-amber-400 text-xs capitalize">
                          {result.type === 'page' && (language === 'tr' ? 'Sayfa' : 'Page')}
                          {result.type === 'product' && (language === 'tr' ? 'Ürün' : 'Product')}
                          {result.type === 'category' && (language === 'tr' ? 'Kategori' : 'Category')}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              ) : searchTerm.length >= 2 ? (
                <div className="p-8 text-center text-slate-400">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{language === 'tr' ? 'Sonuç bulunamadı' : 'No results found'}</p>
                  <p className="text-sm mt-2">
                    {language === 'tr' 
                      ? 'Farklı anahtar kelimeler deneyin' 
                      : 'Try different keywords'
                    }
                  </p>
                </div>
              ) : searchTerm.length > 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <p>{language === 'tr' ? 'En az 2 karakter girin' : 'Enter at least 2 characters'}</p>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-400">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{language === 'tr' ? 'Arama yapmaya başlayın' : 'Start typing to search'}</p>
                  <div className="mt-4 text-sm">
                    <p className="mb-2">{language === 'tr' ? 'Arayabileceğiniz içerikler:' : 'You can search for:'}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <span className="bg-slate-700 px-2 py-1 rounded text-xs">
                        {language === 'tr' ? 'Ürünler' : 'Products'}
                      </span>
                      <span className="bg-slate-700 px-2 py-1 rounded text-xs">
                        {language === 'tr' ? 'Sayfalar' : 'Pages'}
                      </span>
                      <span className="bg-slate-700 px-2 py-1 rounded text-xs">
                        {language === 'tr' ? 'Kategoriler' : 'Categories'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchBar;